import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  UNLIMITED_PRICE_IDS,
  getPlanName,
  handleCheckoutClick,
  isFreePlanEnded,
  isSubscribed,
} from "../utils/Utils";

const NOTICE = {
  unlimited: {
    storageKey: "ers_dismissed_unlimited_price_notice_sept2026_v2",
    title: "Why Unlimited is changing",
    cardCopy: "Starting October 1, Unlimited will be $99/mo.",
    cta: "Why this change",
  },
  free: {
    storageKey: "ers_dismissed_free_plan_notice_sept2026",
    title: "The free plan is ending",
    cardCopy:
      "We're ending support for the free plan. Subscribe to Unlimited by October 1 to keep using the platform.",
    cta: "Why this change",
  },
};

function isDismissed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch (e) {
    return false;
  }
}

function persistDismissed(storageKey) {
  try {
    localStorage.setItem(storageKey, "1");
  } catch (e) {
    // Ignore quota / private-mode failures
  }
}

function getNoticeVariant(account) {
  if (!account) return null;
  if (getPlanName(account) === "Unlimited") return "unlimited";
  if (!isSubscribed(account)) return "free";
  return null;
}

function UnlimitedDialogBody() {
  return (
    <Fragment>
      <p>
        Starting{" "}
        <span className="font-bold underline">October 1</span>, Unlimited
        will be <span className="font-bold">$99</span> per month.
      </p>
      <p>
        We don't take this lightly. Escape Room Studio depends on live game
        infrastructure — always-on servers, media delivery for clues and video,
        and the reliability you need during a show. Those costs have grown as
        we've added features and supported more rooms.
      </p>
      <p>
        This price lets us keep Unlimited stable, supported, and improving for
        companies that run multiple rooms. Thank you for being with us.
      </p>
      <p>
        No action is required on your end. If you have any concerns, please
        reach out to{" "}
        <a
          href="mailto:hudson@escaperoomstudio.com"
          className="font-medium text-gray-900 underline"
        >
          hudson@escaperoomstudio.com
        </a>
        .
      </p>
    </Fragment>
  );
}

function FreeDialogBody() {
  return (
    <Fragment>
      <p>
        We're ending support for the free 1-room plan. Starting{" "}
        <span className="font-bold underline">October 1</span>, you'll need
        an Unlimited subscription to keep using Escape Room Studio.
      </p>
      <p>
        We don't take this lightly. Escape Room Studio depends on live game
        infrastructure — always-on servers, media delivery for clues and video,
        and the reliability you need during a show. Those costs have grown as
        we've added features and supported more rooms, and we can no longer
        sustain a free plan.
      </p>
      <p>
        Subscribe to Unlimited (
        <span className="font-bold">$99</span>/mo) by{" "}
        <span className="font-bold underline">October 1</span> to keep your
        room and continue using the platform.
      </p>
      <p>
        If you have any concerns, please reach out to{" "}
        <a
          href="mailto:hudson@escaperoomstudio.com"
          className="font-medium text-gray-900 underline"
        >
          hudson@escaperoomstudio.com
        </a>
        .
      </p>
    </Fragment>
  );
}

export default function PriceChangeNotice({ account }) {
  const [visible, setVisible] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [variant, setVariant] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    if (isFreePlanEnded()) return;
    const nextVariant = getNoticeVariant(account);
    if (!nextVariant) return;
    if (isDismissed(NOTICE[nextVariant].storageKey)) return;
    setVariant(nextVariant);
    setVisible(true);
  }, [account]);

  const dismiss = () => {
    if (variant) persistDismissed(NOTICE[variant].storageKey);
    setDialogOpen(false);
    setVisible(false);
  };

  const startUnlimitedCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutError(null);
    setCheckoutLoading(true);
    try {
      const stripeId =
        account && account.subscription && account.subscription.customer_id
          ? account.subscription.customer_id
          : null;
      const subscriptionStatus =
        account && account.subscription_status
          ? account.subscription_status.type
          : null;
      await handleCheckoutClick(
        stripeId,
        UNLIMITED_PRICE_IDS[0],
        subscriptionStatus,
        account && (account.id || account.account_id),
        account && account.account_email,
        true
      );
    } catch (error) {
      setCheckoutLoading(false);
      setCheckoutError(
        (error && error.message) || "Unable to start checkout"
      );
    }
  };

  if (!visible || !variant || isFreePlanEnded()) return null;

  const notice = NOTICE[variant];

  return (
    <Fragment>
      <div className="fixed bottom-14 left-4 z-40 w-80 rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <button
          type="button"
          onClick={dismiss}
          className="absolute right-2 top-2 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
          Pricing update
        </p>
        <p className="mt-1 pr-6 text-base text-gray-900">{notice.cardCopy}</p>
        <button
          type="button"
          onClick={() => setDialogOpen(true)}
          className="mt-3 rounded-full bg-gray-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700"
        >
          {notice.cta}
        </button>
      </div>

      <Transition appear show={dialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setDialogOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {notice.title}
                  </Dialog.Title>
                  <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600">
                    {variant === "unlimited" ? (
                      <UnlimitedDialogBody />
                    ) : (
                      <FreeDialogBody />
                    )}
                  </div>
                  {checkoutError && (
                    <p className="mt-4 text-sm text-red-600">{checkoutError}</p>
                  )}
                  <div className="mt-6 flex items-center justify-end gap-3">
                    {variant === "free" && (
                      <button
                        type="button"
                        onClick={startUnlimitedCheckout}
                        disabled={checkoutLoading}
                        className="rounded-full bg-gray-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 disabled:opacity-60"
                      >
                        {checkoutLoading
                          ? "Redirecting…"
                          : "Subscribe to Unlimited"}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDialogOpen(false)}
                      className={
                        variant === "free"
                          ? "rounded-full px-3.5 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-100"
                          : "rounded-full bg-gray-900 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-700"
                      }
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </Fragment>
  );
}
